"use client";

import { useEffect, useState, type ReactNode } from "react";
import Link from "next/link";
import { track } from "@/lib/analytics";
import { CATEGORIES, TEMPLATES } from "@/lib/library";
import { TemplateLibrary } from "./TemplateLibrary";
import { AppMockup, DocMockup, LibraryMockup } from "./mockups";
import type { LpLandingConfig } from "@/lib/lpLandingConfig";
import { shortLead } from "@/lib/copyStyle";

const LIBRARY_COUNT = TEMPLATES.length;

const TEMPLATE_PROOF = [
  "No signup or paywall",
  "Task-specific starting points",
  "Builder available for site-specific RAMS",
  "Competent-person review before use",
];

const TOOL_PROOF = [
  "Site-specific prompts",
  "Hazards, controls and risk ratings",
  "Pre-submission gap checks",
  "Free during early access",
];

const READY_CHECKS = [
  {
    title: "Site and scope",
    body: "Actual address, workface, client or principal contractor, access constraints, interfaces and site rules.",
  },
  {
    title: "Method sequence",
    body: "A numbered sequence a supervisor can brief, with hold points where work must stop or be checked.",
  },
  {
    title: "Controls and evidence",
    body: "Controls beyond PPE where needed, plus permits, inspections, competence, COSHH/SDS and emergency arrangements.",
  },
  {
    title: "Review trail",
    body: "Named competent-person review, worker briefing, sign-off and revision details before the RAMS is used on site.",
  },
];

const BUILDER_STEPS = [
  {
    step: "01",
    title: "Choose the work",
    body: "Start from a RamsDocs task pattern, not a blank document. Pick the trade and job so the document begins with relevant sections.",
  },
  {
    step: "02",
    title: "Make it site-specific",
    body: "Add the details reviewers check: site, workface, supervision, access, permits, COSHH triggers, emergency arrangements and interfaces.",
  },
  {
    step: "03",
    title: "Review and export",
    body: "Resolve the gap checks, review as the competent person and export a clean PDF for principal-contractor review.",
  },
];

const TEMPLATE_CHECKS = [
  {
    title: "Before you use a template",
    points: [
      "Name the site, client, supervisor and responsible person.",
      "Replace generic sequencing with the actual method of work.",
      "Confirm permits, COSHH/SDS, isolation and inspection evidence.",
      "Brief the RAMS, record sign-off and revise it if conditions change.",
    ],
  },
  {
    title: "A good RAMS should show",
    points: [
      "Task-specific hazards with initial and residual risk ratings.",
      "Layered controls before PPE-only measures.",
      "Plant, equipment, competence and emergency arrangements.",
      "Clear stop-work triggers a supervisor can brief.",
    ],
  },
];

const TEMPLATE_DECISIONS = [
  {
    title: "A free template can be enough when",
    points: [
      "The work is routine for your team.",
      "A competent person has time to tailor every section.",
      "The site conditions, interfaces and emergency arrangements are simple.",
      "You need a structured starting point rather than a generated document.",
    ],
  },
  {
    title: "Use the builder when",
    points: [
      "A principal contractor has asked for a job-specific RAMS.",
      "The job involves permits, COSHH, work at height, plant or isolation.",
      "You want prompts for missing evidence before submission.",
      "You produce RAMS often enough that editing Word files is the bottleneck.",
    ],
  },
];

const primaryCta =
  "inline-flex items-center justify-center rounded-lg bg-emerald px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_-10px_rgba(27,163,123,0.8)] transition-colors hover:bg-emerald-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald";
const secondaryCta =
  "inline-flex items-center justify-center rounded-lg border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald";

export function LpLandingView({ cfg, draftIds = [] }: { cfg: LpLandingConfig; draftIds?: string[] }) {
  useEffect(() => {
    track("lp_viewed", { slug: cfg.slug, intent: cfg.intent });
  }, [cfg.slug, cfg.intent]);

  // Echo the searched term back so the page visibly matches the query (the ad passes
  // utm_term={keyword}). Guards against the unreplaced "{keyword}" literal and junk.
  const [matchedTerm, setMatchedTerm] = useState("");
  useEffect(() => {
    try {
      const t = (new URLSearchParams(window.location.search).get("utm_term") || "").trim();
      if (t && t.length <= 60 && !/[<>{}]/.test(t)) setMatchedTerm(t);
    } catch {
      /* no-op */
    }
  }, []);

  const templateIntent = cfg.intent === "template";
  const proof = templateIntent ? TEMPLATE_PROOF : TOOL_PROOF;

  // A/B (template intent only): find-first hero (control) vs build-first hero (test),
  // split 50/50 by the lp-template-layout flag. Defaults to find-first until the flag
  // resolves; reading the flag auto-fires $feature_flag_called (exposure). Compare on
  // template_result_clicked / template_search by the flag variant.
  const [buildFirst, setBuildFirst] = useState(false);
  useEffect(() => {
    if (!templateIntent) return;
    const ph = (window as unknown as { posthog?: { getFeatureFlag?: (k: string) => string | undefined; onFeatureFlags?: (cb: () => void) => void } }).posthog;
    if (!ph) return;
    const read = () => {
      const v = ph.getFeatureFlag?.("lp-template-layout");
      if (v) setBuildFirst(v === "build-first");
    };
    ph.onFeatureFlags?.(read);
    read();
  }, [templateIntent]);

  const heroCtas = !templateIntent
    ? [
        { href: "/builder/", target: "builder", label: "Start building your RAMS", primary: true },
        { href: "/templates/", target: "templates", label: "Browse RAMS templates", primary: false },
      ]
    : buildFirst
      ? [
          { href: "/builder/", target: "builder", label: "Build a site-specific RAMS", primary: true },
          { href: "#template-library", target: "template_search", label: "Search the free templates", primary: false },
        ]
      : [
          { href: "#template-library", target: "template_search", label: "Search the free templates", primary: true },
          { href: "/builder/", target: "builder", label: "Build a site-specific RAMS", primary: false },
        ];
  const heroVariant = templateIntent ? (buildFirst ? "build-first" : "find-first") : undefined;

  return (
    <div className="bg-mist">
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="dotgrid-dark absolute inset-0 opacity-50" />
        <div className="relative mx-auto grid max-w-7xl gap-8 px-5 py-10 lg:grid-cols-[1fr_0.92fr] lg:items-center lg:py-12">
          <div>
            <span className="eyebrow !text-emerald">{templateIntent ? "Free RAMS starting points" : "RAMS tool & generator"}</span>
            <h1 className="mt-4 max-w-3xl text-3xl leading-[1.04] text-white sm:text-4xl lg:text-[3rem]">{cfg.h1}</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/78 sm:text-lg">{shortLead(cfg.sub, 150)}</p>
            {matchedTerm && (
              <p className="mt-3 text-sm text-white/70">
                Matched to your search: <span className="font-medium text-white">&ldquo;{matchedTerm}&rdquo;</span>
              </p>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {heroCtas.map((c) => (
                <Link
                  key={c.target}
                  href={c.href}
                  onClick={() => track("lp_cta_clicked", { slug: cfg.slug, intent: cfg.intent, target: c.target, variant: heroVariant })}
                  className={c.primary ? primaryCta : secondaryCta}
                >
                  {c.label}
                </Link>
              ))}
            </div>

            <p className="mt-4 text-sm leading-relaxed text-white/62">
              Structured around CDM 2015 and relevant HSE guidance. Review against the actual site before use.
            </p>
            {!templateIntent && (
              <p className="mt-3">
                <a
                  href="/sample-rams.pdf"
                  target="_blank"
                  rel="noopener"
                  onClick={() => track("sample_report_clicked", { slug: cfg.slug, intent: cfg.intent })}
                  className="text-sm font-medium text-white/70 underline decoration-white/30 underline-offset-4 hover:text-white"
                >
                  See what the finished RAMS looks like — sample PDF
                </a>
              </p>
            )}

            <ul className="mt-6 grid max-w-2xl gap-2 sm:grid-cols-2">
              {proof.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-white/80">
                  <CheckDot /> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* The mockup reads as real controls — users click its search box.
              Make the whole frame a link so that click lands on the real thing. */}
          <Link
            href={templateIntent ? "#template-library" : "/builder/"}
            onClick={() => track("lp_cta_clicked", { slug: cfg.slug, intent: cfg.intent, target: "hero_mockup" })}
            aria-label={templateIntent ? "Search the template library" : "Open the RAMS builder"}
            className="group hidden md:block lg:pl-2"
          >
            {templateIntent ? <LibraryMockup /> : <AppMockup />}
            <span className="mt-3 block text-center text-sm font-medium text-white/60 group-hover:text-white">
              {templateIntent ? "Search the real library below ↓" : "Open the builder →"}
            </span>
          </Link>
        </div>
      </section>

      <section className="border-b border-line bg-white">
        <div className="mx-auto grid max-w-7xl gap-3 px-5 py-4 sm:grid-cols-3">
          <ProofStat value={`${LIBRARY_COUNT}`} label="RAMS starts" />
          <ProofStat value={`${CATEGORIES.length}`} label="work categories" />
          <ProofStat value="£0" label="during early access" />
        </div>
      </section>

      {templateIntent ? <TemplateLandingBody cfg={cfg} draftIds={draftIds} /> : <ToolLandingBody cfg={cfg} />}
    </div>
  );
}

function TemplateLandingBody({ cfg, draftIds }: { cfg: LpLandingConfig; draftIds: string[] }) {
  return (
    <>
      <section id="template-library" className="scroll-mt-24 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
          <div className="max-w-3xl">
            <span className="eyebrow">Find the closest match</span>
            <h2 className="mt-3 text-3xl md:text-[2.35rem]">Start with the right task, then make it specific</h2>
            <p className="mt-3 text-lg leading-relaxed text-body">Search by trade, task or hazard. Then add the site details that make the RAMS usable.</p>
          </div>
          <div className="mt-8">
            <TemplateLibrary draftIds={draftIds} initialLimit={12} cardsToBuilder />
          </div>
        </div>
      </section>

      <section className="bg-mist">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:py-20">
          <div>
            <span className="eyebrow">Template safety check</span>
            <h2 className="mt-3 text-3xl md:text-[2.25rem]">Do not submit a generic template unchanged</h2>
            <p className="mt-4 text-lg leading-relaxed text-body">Use the template for structure. Add the actual site, sequence, interfaces and controls before submission.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {TEMPLATE_CHECKS.map((group) => (
              <ChecklistCard key={group.title} title={group.title} items={group.points} />
            ))}
          </div>
        </div>
      </section>

      <DecisionSection cfg={cfg} />
      <ReadinessSection />
      <EvidenceSection />
      <FinalCta cfg={cfg} intent="template" />
    </>
  );
}

function ToolLandingBody({ cfg }: { cfg: LpLandingConfig }) {
  return (
    <>
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
          <div className="max-w-3xl">
            <span className="eyebrow">Workflow</span>
            <h2 className="mt-3 text-3xl md:text-[2.35rem]">Build fast, then review properly</h2>
            <p className="mt-3 text-lg leading-relaxed text-body">Start from the job, answer the site prompts and review the document before export.</p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {BUILDER_STEPS.map((step) => (
              <FeatureCard key={step.step} title={step.title} tag={step.step}>
                {step.body}
              </FeatureCard>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-mist">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-2 lg:items-center lg:py-20">
          <DocMockup />
          <div>
            <span className="eyebrow">Review-ready structure</span>
            <h2 className="mt-3 text-3xl md:text-[2.25rem]">The output is built for a site manager to review and brief</h2>
            <p className="mt-4 text-lg leading-relaxed text-body">The output combines risk assessment, method, evidence prompts and briefing record in one document.</p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {[
                "Scope and site details",
                "Numbered method sequence",
                "Hazards, controls and 5x5 scores",
                "PPE, plant and equipment",
                "Permits, COSHH and inspection prompts",
                "Emergency, briefing and sign-off records",
              ].map((item) => (
                <CheckLine key={item}>{item}</CheckLine>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ReadinessSection />
      <EvidenceSection />
      <FinalCta cfg={cfg} intent="tool" />
    </>
  );
}

function DecisionSection({ cfg }: { cfg: LpLandingConfig }) {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
        <div className="max-w-3xl">
          <span className="eyebrow">Template or builder?</span>
          <h2 className="mt-3 text-3xl md:text-[2.35rem]">Choose the route that fits the job risk</h2>
        </div>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {TEMPLATE_DECISIONS.map((group, index) => (
            <PathCard
              key={group.title}
              title={group.title}
              cta={index === 0 ? "Keep browsing templates" : "Build a site-specific RAMS"}
              href={index === 0 ? "#template-library" : "/builder/"}
              onClick={() => track("lp_cta_clicked", { slug: cfg.slug, intent: cfg.intent, target: index === 0 ? "template_decision" : "builder_decision" })}
              items={group.points}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ReadinessSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
        <div className="max-w-3xl">
          <span className="eyebrow">Before it leaves your desk</span>
          <h2 className="mt-3 text-3xl md:text-[2.35rem]">What a RAMS needs before review</h2>
          <p className="mt-3 text-lg leading-relaxed text-body">Add these before a client, PC or site manager reviews it.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {READY_CHECKS.map((item) => (
            <div key={item.title} className="rounded-lg border border-line bg-white p-6 shadow-soft">
              <h3 className="text-lg">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-body">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EvidenceSection() {
  return (
    <section className="bg-ink text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-[0.85fr_1.15fr] lg:items-start lg:py-20">
        <div>
          <span className="eyebrow !text-emerald">What reviewers look for</span>
          <h2 className="mt-3 text-3xl text-white md:text-[2.25rem]">Paperwork that supports the safe system of work</h2>
          <p className="mt-4 text-lg leading-relaxed text-white/72">Make the controls, evidence and method sequence easy to check and brief.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Actual site constraints and workface access",
            "Interfaces with other trades, public or occupants",
            "Permits, isolations, inspections and competent people",
            "COSHH/SDS and emergency or rescue arrangements",
            "Initial and residual risk after controls",
            "Briefing, sign-off, review date and revision history",
          ].map((item) => (
            <div key={item} className="rounded-lg border border-white/10 bg-white/[0.06] p-4 text-sm font-medium leading-relaxed text-white/82">
              {item}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ cfg, intent }: { cfg: LpLandingConfig; intent: "template" | "tool" }) {
  const templateIntent = intent === "template";
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-4xl px-5 py-16 text-center lg:py-20">
        <span className="eyebrow">Free during early access</span>
        <h2 className="mt-3 text-3xl md:text-[2.35rem]">{templateIntent ? "Turn a template into a site-specific RAMS" : "Build the RAMS before the paperwork delays the job"}</h2>
        <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-body">Build the RAMS, check it against the site, then export when it is ready.</p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link
            href="/builder/"
            onClick={() => track("lp_cta_clicked", { slug: cfg.slug, intent: cfg.intent, target: "final_builder" })}
            className="inline-flex items-center justify-center rounded-lg bg-emerald px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-deep"
          >
            Build a RAMS
          </Link>
          <Link
            href={templateIntent ? "#template-library" : "/templates/"}
            onClick={() => track("lp_cta_clicked", { slug: cfg.slug, intent: cfg.intent, target: templateIntent ? "final_template_search" : "final_templates" })}
            className="inline-flex items-center justify-center rounded-lg border border-line-2 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink hover:bg-mist"
          >
            {templateIntent ? "Search templates" : "Browse templates"}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ProofStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-line bg-white px-4 py-3 text-center shadow-soft">
      <div className="font-display text-xl font-bold text-ink">{value}</div>
      <div className="mt-1 text-xs font-medium text-muted">{label}</div>
    </div>
  );
}

function ChecklistCard({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-line bg-white p-6 shadow-soft">
      <h3 className="text-lg font-bold text-ink">{title}</h3>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <CheckLine key={item}>{item}</CheckLine>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ title, tag, children }: { title: string; tag: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-white p-6 shadow-soft">
      <span className="text-xs font-semibold uppercase text-emerald-deep">{tag}</span>
      <h3 className="mt-3 text-lg font-bold text-ink">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-body">{children}</p>
    </div>
  );
}

function PathCard({
  title,
  items,
  cta,
  href,
  onClick,
}: {
  title: string;
  items: string[];
  cta: string;
  href: string;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-line bg-white p-6 shadow-soft">
      <h3 className="text-xl font-bold text-ink">{title}</h3>
      <div className="mt-4 flex-1 space-y-3">
        {items.map((item) => (
          <CheckLine key={item}>{item}</CheckLine>
        ))}
      </div>
      <Link href={href} onClick={onClick} className="mt-6 inline-flex font-semibold text-emerald-deep hover:text-emerald">
        {cta} →
      </Link>
    </div>
  );
}

function CheckLine({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm leading-relaxed text-body">
      <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald text-white">
        <svg viewBox="0 0 20 20" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}

function CheckDot() {
  return (
    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald text-white" aria-hidden="true">
      <svg viewBox="0 0 20 20" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
        <path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
